import { Organization } from 'auth0'
import { combineLatest, EMPTY, Subject } from 'rxjs'
import { catchError, startWith, switchMap, takeUntil, tap } from 'rxjs/operators'

import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core'
import { AuthenticationService, isAccountDiscoveryUnavailable } from '@services/authentication.service'

@Component({
  selector: 'shared-organization-picker',
  templateUrl: './organization-picker.component.html',
  styleUrls: ['./organization-picker.component.css'],
  standalone: false
})
export class OrganizationPickerComponent implements OnDestroy {
  public hasPersonalAccount = false
  public organizations: Organization[] = []
  public userOrg: Organization | undefined
  public accountsState: 'loading' | 'ready' | 'error' = 'loading'
  public accountError = ''
  private readonly retry$ = new Subject<void>()
  private readonly destroy$ = new Subject<void>()
  private accountSubject: string | undefined

  get user$() { return this.authenticationService.user$ }

  constructor(
    public authenticationService: AuthenticationService,
    private readonly cdr: ChangeDetectorRef
  ) {
    combineLatest([
      this.authenticationService.isAuthenticated$,
      this.authenticationService.user$
    ]).pipe(
      switchMap(([isAuthenticated, user]) => {
        if (!isAuthenticated || !user) {
          this.clearAccounts()
          return EMPTY
        }
        const subject = JSON.stringify([user.iss, user.sub])
        if (subject !== this.accountSubject) this.clearAccounts()
        this.accountSubject = subject
        this.userOrg = this.organizations.find(org => org.id === user.org_id)
        return this.retry$.pipe(
          startWith(undefined),
          switchMap(() => {
            this.accountsState = 'loading'
            this.cdr.markForCheck()
            return this.authenticationService.getUserAccounts().pipe(
              tap(({ organizations, personalAccount }) => {
                this.hasPersonalAccount = personalAccount !== null
                this.userOrg = organizations.find(org => org.id === user.org_id)
                this.organizations = [...organizations].sort((a, b) =>
                  (a.display_name ?? a.name ?? '').localeCompare(b.display_name ?? b.name ?? ''))
                this.accountsState = 'ready'
                this.accountError = ''
                this.cdr.markForCheck()
              }),
              catchError(error => {
                const unavailable = isAccountDiscoveryUnavailable(error)
                if (!unavailable) this.clearAccounts()
                this.accountError = unavailable ? 'Accounts are temporarily unavailable.' : 'Account access could not be confirmed.'
                this.accountsState = 'error'
                this.cdr.markForCheck()
                return EMPTY
              })
            )
          })
        )
      }),
      takeUntil(this.destroy$)
    ).subscribe()
  }

  private clearAccounts(): void {
    this.hasPersonalAccount = false
    this.organizations = []
    this.userOrg = undefined
  }

  retryAccounts(): void {
    this.retry$.next()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.retry$.complete()
  }

  /** Login to personal or organization account */
  accountLogin(organization?: Organization) {
    const orgID = organization?.id
    const storedOrigin = sessionStorage.getItem('authRedirectOrigin')
    const target = storedOrigin ? storedOrigin : 'projects'
    sessionStorage.removeItem('authRedirectOrigin')

    this.authenticationService.rememberLoginAccount(organization
      ? { kind: 'organization', organizationId: organization.id }
      : { kind: 'personal' })
    sessionStorage.setItem('redirectDashboard', 'true')
    this.authenticationService.loginWithRedirect({ appState: { target }, authorizationParams: { organization: orgID } })
  }

  navigateToAuthLogin() {
    this.authenticationService.loginWithRedirect({ appState: { target: 'login' }, authorizationParams: { screen_hint: 'login' } })
  }
}
